import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SubLayout from '@/components/feature/SubLayout';
import { supabase } from '@/lib/supabase';
import { useSubcontractorAssignments } from '@/hooks/useSubcontractorAssignments';
import ScheduleTab from '@/pages/management/projects/components/ScheduleTab';
import FilesTab from '@/pages/management/projects/components/FilesTab';
import ChecklistTab from '@/pages/management/projects/components/ChecklistTab';
import { useProjectSummary } from '@/hooks/useProjectSummary';
import { useProjectStages } from '@/hooks/useProjectStages';
import { useTranslation } from 'react-i18next';
import {
  SkeletonHero,
  SkeletonStatRow,
  SkeletonCard,
  SkeletonStageTimeline,
  SkeletonCompactList,
  SkeletonText,
  SkeletonBase,
} from '@/components/base/Skeleton';

const tabs = ['Overview', 'Schedule', 'Checklist', 'Files'];

const stageStatusStyle: Record<string, { bg: string; text: string; border: string; label: string }> = {
  completed:   { bg: '#F0FDF4', text: '#059669', border: '#BBF7D0', label: '' },
  in_progress: { bg: '#FEF0E7', text: '#E8621A', border: '#FDBA74', label: '' },
  not_started: { bg: '#F9FAFB', text: '#9CA3AF', border: '#E5E7EB', label: '' },
  delayed:     { bg: '#FEF2F2', text: '#EF4444', border: '#FECACA', label: '' },
  on_hold:     { bg: '#FFFBEB', text: '#F59E0B', border: '#FDE68A', label: '' },
  active:      { bg: '#FEF0E7', text: '#E8621A', border: '#FDBA74', label: '' },
};
const stageIcon: Record<string, string> = {
  completed: 'ri-check-line',
  in_progress: 'ri-loader-4-line',
  not_started: 'ri-time-line',
  delayed: 'ri-error-warning-line',
  on_hold: 'ri-pause-circle-line',
  active: 'ri-loader-4-line',
};

const FALLBACK_COVER = 'https://readdy.ai/api/search-image?query=modern%20residential%20construction%20site%20New%20Zealand%20home%20building%20timber%20framing%20progress%20professional%20photography%20warm%20natural%20light%20clean%20background%20minimal&width=800&height=500&seq=subprojfall&orientation=landscape';

interface RealProject {
  id: string;
  project_name: string;
  project_code: string;
  site_address: string | null;
  status: string;
  cover_image: string | null;
  client_name: string | null;
  start_date: string | null;
  target_completion_date: string | null;
  summary: string | null;
}

interface Instruction {
  id: string;
  instruction_title: string;
  body: string;
  priority: string;
  status: string;
  requires_response: boolean;
  due_at: string | null;
  created_at: string;
}

interface ProgressUpdate {
  id: string;
  title: string;
  body: string | null;
  update_type: string;
  posted_by_role: string;
  created_at: string;
  stage_name: string | null;
}

export default function SubcontractorProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { projectIds } = useSubcontractorAssignments();

  const { t } = useTranslation();

  // Build dynamic stage status labels using translations
  function fmtDate(d: string | null): string {
    if (!d) return t('sub.project.tbc');
    return new Date(d).toLocaleDateString(t('date.locale') || 'en-NZ', { day: 'numeric', month: 'short' });
  }

  const stageLabels: Record<string, string> = {
    completed: t('sub.project.completedLabel'),
    in_progress: t('sub.project.inProgressLabel'),
    not_started: t('sub.project.upcomingLabel'),
    delayed: t('sub.project.delayedLabel'),
    on_hold: t('sub.project.onHoldLabel'),
    active: t('sub.project.inProgressLabel'),
  };

  const [activeTab, setActiveTab] = useState('Overview');
  const tabLabels: Record<string, string> = {
    'Overview': t('sub.project.overview'),
    'Schedule': t('sub.project.schedule'),
    'Checklist': t('sub.project.checklist'),
    'Files': t('sub.project.files'),
  };

  // Parse tab from URL query param on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam && tabs.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, []);

  const [project, setProject] = useState<RealProject | null>(null);
  const [instructions, setInstructions] = useState<Instruction[]>([]);
  const [updates, setUpdates] = useState<ProgressUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isAssigned = id ? projectIds.includes(id) : false;

  // ─── REAL STAGE DATA — derived from jphph_project_schedule_items ───
  const {
    stages: realStages,
    currentStage,
    projectStageStatus,
    loading: stagesLoading,
  } = useProjectStages(id);

  // ─── UNIFIED: Single source of truth for project progress ───
  const summary = useProjectSummary(id);
  const summaryLoaded = summary.loaded;

  const loadProject = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');

    try {
      const { data: proj, error: projErr } = await supabase
        .from('jphph_projects')
        .select('id, project_name, project_code, site_address, status, cover_image, client_name, start_date, target_completion_date, summary')
        .eq('id', id)
        .maybeSingle();

      if (import.meta.env.DEV) {
        console.log('[SubProjectDetail] project query:', { id, found: !!proj, error: projErr });
      }

      if (projErr) throw projErr;
      if (!proj) {
        setError('Project not found');
        setLoading(false);
        return;
      }
      setProject(proj as RealProject);

      const { data: insRows } = await supabase
        .from('jphph_instructions')
        .select('id, instruction_title, body, priority, status, requires_response, due_at, created_at')
        .eq('project_id', id)
        .order('created_at', { ascending: false });
      setInstructions((insRows || []) as Instruction[]);

      const { data: updRows } = await supabase
        .from('jphph_progress_updates')
        .select('*')
        .eq('project_id', id)
        .order('created_at', { ascending: false })
        .limit(20);
      setUpdates((updRows || []) as ProgressUpdate[]);

      if (import.meta.env.DEV) {
        console.log('[SubProjectDetail] loaded counts:', {
          instructions: (insRows || []).length,
          updates: (updRows || []).length,
        });
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  // Redirect if id is not in assigned project ids
  useEffect(() => {
    if (!id) {
      navigate('/subcontractor/dashboard', { replace: true });
      return;
    }
    if (projectIds.length > 0 && !projectIds.includes(id)) {
      if (import.meta.env.DEV) {
        console.warn('[SubProjectDetail] Route param id not in assigned projects:', id, 'assigned:', projectIds);
      }
      navigate('/subcontractor/dashboard', { replace: true });
    }
  }, [id, projectIds, navigate]);

  // ─── Checklist stats from lightweight project summary (avoid duplicate SSC fetch on page load) ───
  const subPending = summaryLoaded
    ? summary.subcontractorTotal - summary.subcontractorCompleted
    : 0;

  // ─── Unified display values: NEVER show 0 during loading ───
  const unifiedProgress = summaryLoaded ? summary.projectProgress : undefined;
  const unifiedStageName = summaryLoaded && summary.activeStage ? summary.activeStage : currentStage?.stage_name || '—';
  const unifiedSubCompleted = summaryLoaded ? summary.subcontractorCompleted : undefined;
  const unifiedSubTotal = summaryLoaded ? summary.subcontractorTotal : undefined;
  const unifiedSubPct = summaryLoaded ? summary.subPct : undefined;
  const unifiedMgmtCompleted = summaryLoaded ? summary.managementCompleted : undefined;
  const unifiedMgmtTotal = summaryLoaded ? summary.managementTotal : undefined;
  const isCompleted = summaryLoaded && summary.projectProgress >= 100;

  const displayProgress = isCompleted ? 100 : (unifiedProgress ?? undefined);
  const displayProgressLabel = displayProgress !== undefined ? `${displayProgress}%` : '—';
  const displayStage = isCompleted ? 'Completed' : unifiedStageName;

  const coverImage = project?.cover_image || FALLBACK_COVER;

  // ─── First-load detection for skeleton ───
  // Use a ref to ensure skeleton ONLY shows on the very first load,
  // not on background refreshes. Wait for ALL core data sources.
  const firstLoadDoneRef = useRef(false);
  const isFirstLoad = !firstLoadDoneRef.current && (loading || !summaryLoaded || stagesLoading);

  useEffect(() => {
    if (!loading && summaryLoaded && !stagesLoading) {
      firstLoadDoneRef.current = true;
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[SubProjectDetail] FIRST LOAD COMPLETE', {
          projectId: id,
          projectName: project?.project_name,
          progress: summary.projectProgress,
          activeStage: summary.activeStage,
          stageCount: realStages.length,
        });
      }
    }
  }, [loading, summaryLoaded, stagesLoading, id, project?.project_name, summary.projectProgress, summary.activeStage, realStages.length]);

  // ─── SKELETON LOADING STATE ───
  if (isFirstLoad) {
    return (
      <SubLayout showBack backTo="/subcontractor/dashboard">
        <div className="w-full overflow-x-hidden pb-10">
          <SkeletonHero />
          <div className="bg-[#1A1A1A] px-4 py-3">
            <SkeletonStatRow count={4} />
          </div>
          {/* Tab bar */}
          <div className="bg-white border-b border-gray-100 sticky top-14 z-20">
            <div className="flex overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  className={`flex-shrink-0 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === tab ? 'border-[#059669] text-[#059669]' : 'border-transparent text-[#9CA3AF]'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
          <div className="px-4 py-4 max-w-full overflow-x-hidden space-y-4">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonStageTimeline count={5} />
            <div className="grid grid-cols-2 gap-3">
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </div>
        </div>
      </SubLayout>
    );
  }

  if (error || !project) {
    return (
      <SubLayout showBack backTo="/subcontractor/dashboard">
        <div className="text-center py-20 px-4">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
            <i className="ri-building-2-line text-5xl"></i>
          </div>
          <p className="text-[#374151] font-semibold mb-1">{error || t('sub.project.projectNotFound')}</p>
          <button
            onClick={() => navigate('/subcontractor/dashboard')}
            className="mt-3 text-[#E8621A] text-sm font-medium cursor-pointer"
          >
            {t('sub.project.backToDashboard2')}
          </button>
        </div>
      </SubLayout>
    );
  }

  return (
    <SubLayout showBack backTo="/subcontractor/dashboard">
      <div className="w-full overflow-x-hidden pb-10">
        {/* Hero */}
        <div className="relative h-52 overflow-hidden">
          <img
            src={coverImage}
            alt={project.project_name}
            className="w-full h-full object-cover object-center"
            onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_COVER; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/90 via-[#1A1A1A]/30 to-transparent"></div>
          <button
            onClick={() => navigate('/subcontractor/dashboard')}
            className="absolute top-4 left-4 w-9 h-9 flex items-center justify-center rounded-full bg-black/30 backdrop-blur-sm text-white cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-bold text-white/50 uppercase tracking-wider">{project.project_code}</span>
              <span className="text-white/30">·</span>
              <span className="text-[10px] font-bold text-[#E8621A] capitalize">
                {summaryLoaded ? summary.projectStatus : project.status}
              </span>
              {summaryLoaded && summary.activeStage && (
                <>
                  <span className="text-white/30">·</span>
                  <span className="text-[10px] font-bold text-white/80">{summary.activeStage}</span>
                </>
              )}
            </div>
            <h1 className="text-white text-xl font-bold leading-tight">{project.project_name}</h1>
            <p className="text-white/60 text-xs mt-0.5">{project.site_address || '—'}</p>
          </div>
        </div>

        {/* Stats row */}
        <div className="bg-[#1A1A1A] px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { label: t('sub.project.myChecklist'), value: (unifiedSubTotal ?? 0) > 0 ? `${unifiedSubCompleted}/${unifiedSubTotal}` : '—', color: displayProgress === 100 ? '#059669' : '#E8621A', sub: (unifiedSubTotal ?? 0) > 0 ? `${unifiedSubPct}% done` : '' },
            ...(subPending > 0 ? [{ label: t('sub.project.pending'), value: `${subPending}`, color: '#F59E0B', sub: t('sub.project.awaitingYourCheck') }] : []),
            { label: t('sub.project.finalApproval'), value: displayProgress !== undefined ? displayProgressLabel : '—', color: displayProgress === 100 ? '#059669' : '#E8621A', sub: summaryLoaded ? `${unifiedMgmtCompleted}/${unifiedMgmtTotal}` : '—' },
            { label: t('sub.project.overview'), value: displayStage, color: displayProgress === 100 ? '#059669' : '#059669', sub: null },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-base font-bold leading-tight" style={{ color: stat.color }}>{stat.value}</div>
              <div className="text-[#9CA3AF] text-[10px] mt-0.5">{stat.label}</div>
              {stat.sub && (
                <div className="text-[9px] mt-0.5" style={{ color: stat.color }}>{stat.sub}</div>
              )}
            </div>
          ))}
        </div>

        {/* Pending banner */}
        {subPending > 0 && (
          <div className="bg-[#FEF0E7] border border-[#FDBA74] rounded-2xl p-4 mx-4 mt-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#FFFBEB] border border-[#F59E0B]/30 flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-[#F59E0B] text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#1A1A1A] text-sm font-semibold">
                  {t('sub.project.pendingChecks', { count: subPending, s: subPending === 1 ? 'check' : 'checks' })}
                </p>
                <p className="text-[#92400E] text-xs mt-0.5 leading-relaxed">
                  {t('sub.project.pendingChecksDesc')}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => setActiveTab('Checklist')}
                    className="px-3 py-1.5 rounded-lg bg-[#059669] text-white text-xs font-bold cursor-pointer whitespace-nowrap"
                  >
                    {t('sub.project.goToChecklist')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab bar */}
        <div className="bg-white border-b border-gray-100 sticky top-14 z-20">
          <div className="flex overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-shrink-0 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab ? 'border-[#059669] text-[#059669]' : 'border-transparent text-[#9CA3AF]'}`}
              >
                {tabLabels[tab] ?? tab}
              </button>
            ))}
          </div>
        </div>

        <div className="px-4 py-4 max-w-full overflow-x-hidden">
          {/* ── Overview Tab ── */}
          {activeTab === 'Overview' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-gray-100 p-4">
                <h2 className="font-bold text-[#1A1A1A] text-sm mb-4">{t('sub.project.constructionJourney')}</h2>
                {stagesLoading && realStages.length === 0 ? (
                  <SkeletonStageTimeline count={5} />
                ) : realStages.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-[#9CA3AF] text-sm">{t('sub.project.noStages')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {realStages.map((stage, i) => {
                      const style = stageStatusStyle[stage.status] || stageStatusStyle.not_started;
                      const label = stageLabels[stage.status] || stage.status;
                      const isLast = i === realStages.length - 1;
                      return (
                        <div key={stage.id} className="flex items-start gap-3">
                          <div className="flex flex-col items-center flex-shrink-0 w-8">
                            <div
                              className="w-9 h-9 rounded-full flex items-center justify-center border-2 text-[10px] font-bold flex-shrink-0 z-10"
                              style={{ backgroundColor: style.bg, borderColor: style.border, color: style.text }}
                            >
                              {stage.status === 'completed' ? <i className="ri-check-line text-xs"></i> : i + 1}
                            </div>
                            {!isLast && <div className="w-0.5 flex-1 min-h-[20px]" style={{ backgroundColor: stage.status === 'completed' ? '#BBF7D0' : '#E5E7EB' }}></div>}
                          </div>
                          <div className="flex-1 min-w-0 pb-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-[#1A1A1A] text-sm font-semibold truncate">{stage.stage_name}</span>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: style.bg, color: style.text }}>
                                  {label}
                                </span>
                              </div>
                              {stage.status !== 'not_started' && stage.completion_percent > 0 && (
                                <span className="text-[#9CA3AF] text-xs font-medium flex-shrink-0">{stage.completion_percent}%</span>
                              )}
                            </div>
                            <div className="text-[#9CA3AF] text-xs mt-0.5">
                              {stage.start_date
                                ? `${fmtDate(stage.start_date)} — ${fmtDate(stage.end_date)}`
                                : t('sub.project.datesTBC')
                              }
                            </div>
                            {stage.item_count > 0 && (
                              <div className="text-[#9CA3AF] text-[10px] mt-0.5">
                                {t('sub.common.itemsCompleted', { done: stage.completed_items, total: stage.item_count })}
                              </div>
                            )}
                            {(stage.status === 'in_progress' || stage.status === 'active') && stage.completion_percent > 0 && (
                              <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full bg-[#E8621A]" style={{ width: `${stage.completion_percent}%` }}></div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-4">
                <h2 className="font-bold text-[#1A1A1A] text-sm mb-3">{t('sub.project.projectInfo')}</h2>
                <div className="space-y-2.5">
                  {[
                    { label: t('sub.project.site'), value: project.site_address || '—' },
                    { label: t('sub.project.startDate'), value: project.start_date ? new Date(project.start_date).toLocaleDateString(t('date.locale') || 'en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }) : t('sub.project.tbc') },
                    { label: t('sub.project.estCompletion'), value: project.target_completion_date ? new Date(project.target_completion_date).toLocaleDateString(t('date.locale') || 'en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }) : t('sub.project.tbc') },
                    { label: t('client.portal.status'), value: project.status },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between">
                      <span className="text-[#9CA3AF] text-sm">{row.label}</span>
                      <span className="text-[#374151] text-sm font-medium capitalize">{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {updates.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-4">
                  <h2 className="font-bold text-[#1A1A1A] text-sm mb-3">{t('sub.project.recentUpdates')}</h2>
                  <div className="space-y-3">
                    {updates.slice(0, 3).map((update) => (
                      <div key={update.id} className="flex items-start gap-3 pb-3 border-b border-gray-50 last:border-0 last:pb-0">
                        <div className="w-8 h-8 rounded-lg bg-[#ECFDF5] flex items-center justify-center flex-shrink-0">
                          <i className="ri-tools-line text-sm text-[#059669]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#374151] text-sm font-semibold leading-snug line-clamp-1">{update.title}</p>
                          <p className="text-[#9CA3AF] text-xs mt-0.5">
                            {update.stage_name || t('sub.project.generalUpdate')} · {new Date(update.created_at).toLocaleDateString(t('date.locale') || 'en-NZ', { day: 'numeric', month: 'short' })}
                          </p>
                          {update.body && <p className="text-[#6B7280] text-xs mt-1 leading-relaxed line-clamp-2">{update.body}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {instructions.filter((i) => i.status !== 'resolved').length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="font-bold text-[#1A1A1A] text-sm">{t('sub.project.activeInstructions')}</h2>
                    <button
                      onClick={() => navigate('/subcontractor/instructions')}
                      className="text-[#E8621A] text-xs font-semibold cursor-pointer whitespace-nowrap"
                    >
                      {t('sub.project.viewAll')} <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                  <div className="space-y-2">
                    {instructions.filter((i) => i.status !== 'resolved').slice(0, 3).map((ins) => (
                      <div key={ins.id} className="flex items-start gap-3 p-3 rounded-xl bg-[#F9FAFB]">
                        <i className="ri-file-list-3-line text-xs mt-0.5 text-[#9CA3AF]"></i>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#374151] text-sm font-medium leading-snug truncate">{ins.instruction_title}</p>
                          <p className="text-[#9CA3AF] text-xs mt-0.5">{ins.body.slice(0, 80)}{ins.body.length > 80 ? '...' : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick links */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => navigate('/subcontractor/daily-log')}
                  className="bg-white rounded-2xl border border-gray-100 p-4 text-left cursor-pointer hover:border-[#059669]/30 transition-colors"
                >
                  <div className="w-9 h-9 rounded-xl bg-[#ECFDF5] flex items-center justify-center mb-3">
                    <i className="ri-calendar-line text-lg text-[#059669]"></i>
                  </div>
                  <div className="text-[#1A1A1A] text-sm font-semibold">{t('sub.project.dailyLog')}</div>
                  <div className="text-[#9CA3AF] text-xs mt-0.5">{t('sub.project.dailyLogDesc2')}</div>
                </button>
                <button
                  onClick={() => navigate('/subcontractor/instructions')}
                  className="bg-white rounded-2xl border border-gray-100 p-4 text-left cursor-pointer hover:border-[#E8621A]/30 transition-colors"
                >
                  <div className="w-9 h-9 rounded-xl bg-[#FEF0E7] flex items-center justify-center mb-3">
                    <i className="ri-file-list-3-line text-lg text-[#E8621A]"></i>
                  </div>
                  <div className="text-[#1A1A1A] text-sm font-semibold">{t('sub.project.instructions')}</div>
                  <div className="text-[#9CA3AF] text-xs mt-0.5">{t('sub.project.instructionsDesc2')}</div>
                </button>
              </div>
            </div>
          )}

          {/* ── Schedule Tab ── */}
          {activeTab === 'Schedule' && (
            <div className="flex flex-col min-h-[calc(100vh-14rem)] -mx-4">
              <ScheduleTab projectId={project.id} canEdit={false} viewerRole="subcontractor" />
            </div>
          )}

          {/* ── Checklist Tab ── */}
          {activeTab === 'Checklist' && <ChecklistTab projectId={project.id} viewerRole="subcontractor" />}

          {/* ── Files Tab ── */}
          {activeTab === 'Files' && <FilesTab projectId={project.id} canUpload={false} />}
        </div>
      </div>
    </SubLayout>
  );
}